"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Eye, EyeOff, Loader2 } from "lucide-react"
import ForgotPasswordDialog from "./forgot-password-dialog"
import { authAPI } from "@/lib/api"
import { useAuthContext } from "@/context/auth-context"
import LegalConsentDialog from "./legal-consent-dialog"

const loginSchema = z.object({
  email: z.string().email({ message: "Bitte geben Sie eine gültige E-Mail-Adresse ein" }),
  password: z.string().min(6, { message: "Passwort muss mindestens 6 Zeichen haben" }),
})

type LoginFormValues = z.infer<typeof loginSchema>

export default function LoginForm() {
  const router = useRouter()
  const { refresh } = useAuthContext()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false)
  const [loginError, setLoginError] = useState("")
  const [consentOpen, setConsentOpen] = useState(false)
  const [requirements, setRequirements] = useState<Array<{ docType: 'terms' | 'privacy'; version: string; title: string; contentHtml: string }>>([])

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  // Reset error when user starts typing
  const handleInputChange = () => {
    if (loginError) {
      setLoginError("")
    }
  }

  async function onSubmit(data: LoginFormValues) {
    try {
      setIsLoading(true)
      setLoginError("") // Reset error
      const response = await authAPI.login(data.email, data.password)
      if (response.requiresConsent) {
        setRequirements(response.requirements || [])
        setConsentOpen(true)
        return
      }
      await refresh()
      router.push("/")
    } catch (error: any) {
      // Handle different error status codes
      if (error.response?.status === 403) {
        setLoginError("Ihr Account wurde deaktiviert. Bitte wenden Sie sich an den Administrator.")
      } else if (error.response?.status === 401) {
        setLoginError("Die eingegebenen Anmeldedaten sind ungültig.")
      } else if (error.response?.data?.error) {
        setLoginError(error.response.data.error)
      } else {
        setLoginError("Fehler bei der Anmeldung. Bitte versuchen Sie es erneut.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="relative">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
            <div className="flex flex-col items-center space-y-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Anmeldung läuft...</p>
            </div>
          </div>
        )}
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} method="post" className="space-y-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-Mail</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="ihre.email@beispiel.de" 
                      {...field} 
                      autoComplete="email" 
                      disabled={isLoading}
                      onChange={(e) => {
                        field.onChange(e)
                        handleInputChange()
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Passwort</FormLabel>
                    <Button
                      variant="link"
                      className="px-0 text-xs font-normal h-auto"
                      type="button"
                      onClick={() => setForgotPasswordOpen(true)}
                      disabled={isLoading}
                    >
                      Passwort vergessen?
                    </Button>
                  </div>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        {...field}
                        autoComplete="current-password"
                        disabled={isLoading}
                        onChange={(e) => {
                          field.onChange(e)
                          handleInputChange()
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="sr-only">{showPassword ? "Passwort verbergen" : "Passwort anzeigen"}</span>
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Anmelden...
                </>
              ) : (
                "Anmelden"
              )}
            </Button>

            {loginError && (
              <div className="text-sm text-destructive text-center mt-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                {loginError}
              </div>
            )}
          </form>
        </Form>
      </div>

      <LegalConsentDialog 
        open={consentOpen} 
        onOpenChange={setConsentOpen}
        email={form.getValues("email")}
        password={form.getValues("password")}
        requirements={requirements}
        onSuccess={async () => { await refresh(); router.push("/") }}
        onCancel={() => { setConsentOpen(false); setIsLoading(false); }}
      />

      <ForgotPasswordDialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen} />
    </>
  )
}

